import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';

export interface TableColumn {
    key: string;
    value?: string;
    label: string;
    type?: 'text' | 'date' | 'link' | 'badge' | 'switch' | 'checkbox' | 'actions' | 'template' | 'dot';
    templateKey?: string;
    badgeColors?: { [key: string]: string }; // e.g., { High: 'red', Low: 'green' }
    linkKey?: string; // used for 'link' type
}

@Component({
    selector: 'app-table',
    templateUrl: './app-table.component.html',
    styleUrls: ['./app-table.component.scss']
})
export class AppTableComponent {
    @Input() columns: TableColumn[] = [];
    @Input() data: any[] = [];
    @Input() paginatedData: any[] = [];
    @Input() customTemplates: { [key: string]: TemplateRef<any> } = {};
    @Input() hasDot: boolean = true;
    @Input() riskString: string = '%';
    @Input() hasCheckboxes: boolean = false;
    @Input() userInterfaceDisplay: string;
    @Input() originalityRiskFactorHighRisk: number;
    @Input() originalityRiskFactorMediumRisk: number;
    @Input() decimalPlaces: number = 2;
    @Input() checkPublicAccess: boolean = false;
    // @Input() selectionType: 'none' | 'checkbox' = 'none';
    @Output() selectionChange = new EventEmitter<any[]>();
    @Output() switchToggled = new EventEmitter<{ row: any, key: string, value: boolean }>();
    @Output() selectAllChanged = new EventEmitter<boolean>();

    private tooltipCache = new Map<string, string>();
    private colorCache = new Map<string, string>();

    onSwitchToggle(row: any, key: string, event: Event): void {
        const isChecked = (event.target as HTMLInputElement).checked;
        row[key] = isChecked;
        this.switchToggled.emit({ row, key, value: isChecked });
    }

    truncateText(value: string, limit: number = 12): string {
        if (!value) return '';
        return value.length > limit ? value.substring(0, limit) + '...' : value;
    }

    getDotColor(source: string): string {
        if (this.colorCache.has(source)) {
            return this.colorCache.get(source)!;
        }

        const color = (() => {
            switch (source) {
                case 'IA': return '#F2A202';
                case 'IO': return '#007BF5';
                case 'LMS': return '#147B50';
                default: return '#BCBCBC';                     // fallback grey
            }
        })();

        this.colorCache.set(source, color);
        return color;
    }

    getDotTooltip(source: string): string {
        if (this.tooltipCache.has(source)) {
            return this.tooltipCache.get(source)!;
        }

        const tooltip = (() => {
            switch (source) {
                case 'IA': return 'Inspera Assessment';
                case 'IO': return 'Inspera Originality';
                case 'LMS': return 'Learning Management System';
                default: return source || 'Unknown Platform';
            }
        })();

        this.tooltipCache.set(source, tooltip);
        return tooltip;
    }
    ngOnInit() {
        console.log('AppTableComponent loaded');
        console.log(this.columns, " columns in app-table component");
        console.log(this.data, " data in app-table component");
    }

    allSelected: boolean = false;

    toggleAll(event: any) {
        const checked = event.target.checked;
        console.log(this.data, " data in app-table component");

        this.allSelected = checked;
        this.paginatedData.forEach(row => row.selected = checked);
        this.selectionChange.emit(this.getSelectedRows());
        this.selectAllChanged.emit(checked);
    }

    onSelectionChange(row: any) {
        this.allSelected = this.paginatedData.every(row => row.selected);
        this.selectionChange.emit(this.getSelectedRows());
    }

    getSelectedRows(): any[] {
        return this.paginatedData.filter(row => row.selected);
    }

    getRiskLevelLabel(value: any, processed?: number, isPublic?: boolean): 'High' | 'Medium' | 'Low' | 'NA' | 'Processing' | 'CodeSubmissions' {
        // Check if document is still processing
        if (processed === 0 || processed === -1 || processed === 2) {
            return 'Processing';
        }

        if (this.checkPublicAccess && (isPublic === false || isPublic === null)) {
            return 'Processing';
        }

        if (value === 'High' || value === 'Medium' || value === 'Low' || value === 'NA') {
            return value;
        } else {
            const percentage = parseFloat(value);

            if (isNaN(percentage)) return value; // If already a string like "High"

            if (percentage >= this.originalityRiskFactorHighRisk) {
                return 'High';
            } else if (percentage >= this.originalityRiskFactorMediumRisk) {
                return 'Medium';
            } else {
                return 'Low';
            }
        }
    }

    currentPage = 1;
    itemsPerPage = 10;


    totalPages() {
        return Math.ceil(this.data.length / this.itemsPerPage);
    }

    setPage(p: number) {
        this.currentPage = p;
    }
    getDisplayRangeEnd(): number {
        return Math.min(this.currentPage * this.itemsPerPage, this.data.length);
    }

    prevPage() {
        if (this.currentPage > 1) this.currentPage--;
    }

    nextPage() {
        if (this.currentPage < this.totalPages()) this.currentPage++;
    }

    getBadgeColor(col: any, row: any): string {
        if (col.type !== 'badge' || !col.badgeColors) {
            const value = row[col.key];
            return col.badgeColors && col.badgeColors[value] ? col.badgeColors[value] : '#ccc';
        }
    }
}
